import { createSignal } from 'solid-js';

export interface DashboardShareRouteSnapshot {
  projectId: number;
  className: string;
  datasetId: number;
  date: string;
  menu: string;
  page: string;
  sourceId: number;
}

export interface DashboardShareLifecycle {
  shareId: string;
  route: DashboardShareRouteSnapshot;
  pageKey: string;
}

const [pendingShareObject, setPendingShareObjectSignal] = createSignal<Record<string, unknown> | null>(null);
const [shareLifecycle, setShareLifecycleSignal] = createSignal<DashboardShareLifecycle | null>(null);
const [shareBootstrapComplete, setShareBootstrapCompleteSignal] = createSignal(false);

export function setPendingShareObject(object: Record<string, unknown> | null): void {
  setPendingShareObjectSignal(object);
}

export function getPendingShareObject(): Record<string, unknown> | null {
  return pendingShareObject();
}

export function clearPendingShareObject(): void {
  setPendingShareObjectSignal(null);
}

export function startShareUrlLifecycle(shareId: string, object: Record<string, unknown>): void {
  const projectId = Number(object.project_id ?? object.projectId ?? 0);
  setShareLifecycleSignal({
    shareId,
    pageKey: typeof object.page === 'string' ? object.page : '',
    route: {
      projectId: Number.isFinite(projectId) ? projectId : 0,
      className: typeof object.class_name === 'string' ? object.class_name : typeof object.className === 'string' ? object.className : '',
      datasetId: Number(object.dataset_id ?? object.datasetId ?? 0) || 0,
      date: typeof object.date === 'string' ? object.date : '',
      menu: typeof object.selectedMenu === 'string' ? object.selectedMenu : typeof object.menu === 'string' ? object.menu : '',
      page: typeof object.selectedPage === 'string' ? object.selectedPage : typeof object.page_name === 'string' ? object.page_name : '',
      sourceId: Number(object.source_id ?? object.sourceId ?? 0) || 0,
    },
  });
}

export function getShareLifecycle(): DashboardShareLifecycle | null {
  return shareLifecycle();
}

export function clearShareUrlLifecycle(): void {
  setShareLifecycleSignal(null);
}

export function markShareBootstrapComplete(): void {
  setShareBootstrapCompleteSignal(true);
}

export function isShareBootstrapComplete(): boolean {
  return shareBootstrapComplete();
}

export function resetShareBootstrapState(): void {
  setShareBootstrapCompleteSignal(false);
  clearPendingShareObject();
  clearShareUrlLifecycle();
}

function normalizeMenuName(menu: string): string {
  return (menu || '').replace(/\s+/g, '').toUpperCase();
}

export function routeMatchesShareLifecycle(route: DashboardShareRouteSnapshot): boolean {
  const lifecycle = shareLifecycle();
  if (!lifecycle) {
    return true;
  }
  const expected = lifecycle.route;
  return (
    route.projectId === expected.projectId &&
    normalizeMenuName(route.menu) === normalizeMenuName(expected.menu) &&
    route.page === expected.page &&
    route.datasetId === expected.datasetId &&
    (route.date || '') === (expected.date || '') &&
    route.sourceId === expected.sourceId
  );
}
